import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, Brain, Trophy, Users, Sparkles, Flame, Star, Menu, X,
  Languages, Mic, RefreshCw, ChevronDown, Smartphone,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Logo } from "@/components/Logo";
import { GamifiedButton } from "@/components/GamifiedButton";

const STEPS = [
  { icon: Users, title: "Choisis ton profil", desc: "Élève, parent ou professeur — une expérience adaptée à chacun." },
  { icon: Languages, title: "Apprends à lire", desc: "De l'alphabet arabe aux mots, en 3 niveaux progressifs." },
  { icon: BookOpen, title: "Mémorise & récite", desc: "Verset par verset ou sourate entière, avec audio et évaluation." },
  { icon: Trophy, title: "Progresse & débloque", desc: "Gagne des points, des étoiles et des badges à chaque séance." },
];

const BENEFITS = [
  { icon: Brain, title: "Répétition espacée", desc: "Un algorithme te fait réviser au bon moment pour une mémorisation efficace et durable.", tint: "bg-primary/10 text-primary" },
  { icon: Trophy, title: "Gamification", desc: "Badges, séries et progression visuelle : reste motivé jour après jour.", tint: "bg-accent/15 text-yellow-600" },
  { icon: Users, title: "Suivi multi-profils", desc: "Parents et professeurs suivent la progression de leurs enfants et élèves.", tint: "bg-secondary/10 text-secondary" },
];

const FAQ = [
  { q: "À quel âge convient l'application ?", a: "Elle est pensée pour les enfants, les adolescents et les adultes. Les boutons et l'interface sont adaptés aux plus jeunes, avec un mode enfant." },
  { q: "Fonctionne-t-elle sur mobile ?", a: "Oui, l'application est 100 % responsive (smartphone, tablette, ordinateur) et une version native Android/iOS est disponible." },
  { q: "Faut-il un compte pour essayer ?", a: "Non : clique sur « Voir la démo » pour explorer l'app avec le profil de démonstration, sans inscription." },
];

const FaqItem = ({ item, open, onToggle, idx }) => (
  <div className="border border-slate-100 rounded-2xl bg-white overflow-hidden" data-testid={`faq-${idx}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between gap-3 p-5 text-left font-extrabold text-slate-800">
      {item.q}
      <ChevronDown className={`w-5 h-5 text-primary shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="px-5 pb-5 text-slate-500 text-sm leading-relaxed -mt-1">{item.a}</div>}
  </div>
);

function PhoneMockup() {
  return (
    <div className="relative mx-auto w-[260px] sm:w-[300px]">
      <div className="rounded-[2.5rem] border-8 border-slate-900 bg-slate-900 shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-primary to-secondary p-5 text-white relative">
          <div className="absolute inset-0 islamic-star-pattern opacity-30" />
          <div className="relative">
            <p className="text-white/80 text-xs font-semibold">As-salamu alaykum,</p>
            <p className="text-xl font-black mb-3">Bonjour Ahmed 👋</p>
            <div className="inline-flex items-center gap-1.5 bg-white/20 px-3 py-1 rounded-full text-xs font-bold">
              <Flame className="w-3.5 h-3.5" /> Série 4 jours
            </div>
          </div>
        </div>
        <div className="bg-background p-4 space-y-3">
          {[
            { icon: Languages, label: "Apprendre à lire", tint: "bg-primary/10 text-primary" },
            { icon: BookOpen, label: "Nouveau verset", tint: "bg-secondary/10 text-secondary" },
            { icon: RefreshCw, label: "Révisions du jour", tint: "bg-emerald-100 text-emerald-600" },
            { icon: Mic, label: "Récitation", tint: "bg-accent/15 text-yellow-600" },
          ].map((r) => (
            <div key={r.label} className="flex items-center gap-3 bg-white p-3 rounded-2xl border border-slate-100">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${r.tint}`}><r.icon className="w-4 h-4" /></div>
              <span className="text-sm font-bold text-slate-700">{r.label}</span>
            </div>
          ))}
          <div className="grid grid-cols-3 gap-2 pt-1">
            {[{ i: Star, v: "12" }, { i: Flame, v: "4" }, { i: Trophy, v: "3" }].map((s, k) => (
              <div key={k} className="bg-white rounded-2xl border border-slate-100 py-2 text-center">
                <s.i className="w-4 h-4 mx-auto text-secondary" />
                <div className="text-sm font-black text-slate-800">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  useSeo("Apprendre & mémoriser le Coran", "Coach Quran : apprends à lire l'arabe et mémorise le Coran grâce à la répétition espacée et la gamification. Espaces élève, parent et professeur. Essaie gratuitement.");
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);
  const [demoLoading, setDemoLoading] = useState(false);

  const tryDemo = async () => {
    setDemoLoading(true);
    try { await demoLogin(); navigate("/accueil"); }
    catch { setDemoLoading(false); }
  };

  const scrollTo = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };

  return (
    <div className="min-h-screen islamic-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-6 text-sm font-bold text-slate-600">
            <button onClick={() => scrollTo("features")} className="hover:text-primary" data-testid="nav-features">Fonctionnalités</button>
            <Link to="/login" className="hover:text-primary" data-testid="nav-login">Se connecter</Link>
            <GamifiedButton className="py-2.5 px-5 text-sm" onClick={() => navigate("/register")} data-testid="header-cta">
              Essayer gratuitement
            </GamifiedButton>
          </nav>
          <button className="md:hidden text-slate-600" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white px-4 py-4 space-y-3">
            <button onClick={() => scrollTo("features")} className="block w-full text-left font-bold text-slate-600">Fonctionnalités</button>
            <Link to="/login" className="block font-bold text-slate-600" data-testid="mobile-nav-login">Se connecter</Link>
            <GamifiedButton className="w-full" onClick={() => navigate("/register")}>Essayer gratuitement</GamifiedButton>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <span className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-bold mb-5">
            <Sparkles className="w-4 h-4" /> Ton compagnon intelligent du Coran
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-tight mb-5">
            Apprends à lire et mémorise le Coran, à ton rythme.
          </h1>
          <p className="text-lg text-slate-500 mb-8 max-w-xl">
            Apprenez et mémorisez le Coran avec la <strong className="text-slate-700">répétition espacée</strong> et
            la <strong className="text-slate-700">gamification</strong> — de l'alphabet arabe à la récitation complète.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <GamifiedButton className="px-8 py-4 text-lg" onClick={() => navigate("/register")} data-testid="hero-cta-register">
              Essayer gratuitement
            </GamifiedButton>
            <GamifiedButton variant="ghost" className="px-8 py-4 text-lg" onClick={tryDemo} disabled={demoLoading} data-testid="hero-cta-demo">
              <Sparkles className="w-5 h-5" /> {demoLoading ? "Chargement…" : "Voir la démo"}
            </GamifiedButton>
          </div>
          <p className="text-xs text-slate-400 mt-4">Gratuit pour commencer · Aucune carte requise · Démo sans inscription</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }}>
          <PhoneMockup />
        </motion.div>
      </section>

      {/* How it works */}
      <section id="features" className="bg-white border-y border-slate-100 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-black text-slate-900 text-center mb-3">Comment ça marche</h2>
          <p className="text-slate-500 text-center mb-12 max-w-xl mx-auto">Un parcours simple et motivant, en quelques étapes.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="text-center" data-testid={`step-${i}`}>
                <div className="relative w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <s.icon className="w-8 h-8" />
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary text-white text-xs font-black flex items-center justify-center">{i + 1}</span>
                </div>
                <h3 className="font-extrabold text-slate-800 mb-1">{s.title}</h3>
                <p className="text-sm text-slate-500">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 text-center mb-12">Pourquoi Coach Quran ?</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="bg-white p-7 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid={`benefit-${i}`}>
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${b.tint}`}><b.icon className="w-7 h-7" /></div>
              <h3 className="font-black text-slate-800 text-lg mb-2">{b.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Demo highlight */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-secondary text-white p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center">
          <div className="absolute inset-0 islamic-star-pattern opacity-25" />
          <div className="relative">
            <h2 className="text-3xl font-black mb-3">Essaie sans t'inscrire</h2>
            <p className="text-white/80 mb-6">Découvre l'application avec le profil de démonstration « Ahmed » : sourates, progression, révisions et badges — en un clic.</p>
            <GamifiedButton variant="accent" className="px-8 py-4 text-lg" onClick={tryDemo} disabled={demoLoading} data-testid="demo-section-cta">
              <Sparkles className="w-5 h-5" /> {demoLoading ? "Chargement…" : "Lancer la démo Ahmed"}
            </GamifiedButton>
          </div>
          <div className="relative flex justify-center"><PhoneMockup /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 text-center mb-10">Questions fréquentes</h2>
        <div className="space-y-3">
          {FAQ.map((item, i) => (
            <FaqItem key={i} idx={i} item={item} open={faqOpen === i} onToggle={() => setFaqOpen(faqOpen === i ? -1 : i)} />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="text-center bg-white rounded-3xl border border-slate-100 p-10">
          <Smartphone className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="text-3xl font-black text-slate-900 mb-3">Prêt à commencer ton parcours ?</h2>
          <p className="text-slate-500 mb-6">Rejoins Coach Quran gratuitement, sur web et mobile.</p>
          <GamifiedButton className="px-10 py-4 text-lg" onClick={() => navigate("/register")} data-testid="final-cta">Essayer gratuitement</GamifiedButton>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-3 gap-8">
          <div>
            <Logo className="[&_div:first-child>div]:text-white [&_.text-slate-800]:text-white" />
            <p className="text-sm text-slate-400 mt-3 max-w-xs">Ton compagnon intelligent pour apprendre à lire et mémoriser le Coran.</p>
          </div>
          <div>
            <p className="font-extrabold text-white mb-3">Liens</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/cgu" className="hover:text-white" data-testid="footer-cgu">Conditions d'utilisation</Link></li>
              <li><Link to="/confidentialite" className="hover:text-white" data-testid="footer-privacy">Politique de confidentialité</Link></li>
              <li><Link to="/login" className="hover:text-white">Se connecter</Link></li>
              <li><Link to="/register" className="hover:text-white">Créer un compte</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-extrabold text-white mb-3">Suivez-nous</p>
            <p className="text-sm text-slate-400">Contact & réseaux sociaux bientôt disponibles.</p>
          </div>
        </div>
        <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Coach Quran. Tous droits réservés. Données coraniques via AlQuran Cloud.
        </div>
      </footer>
    </div>
  );
}
